import { Trans, useLingui } from '@lingui/react/macro'
import { CantileverButton } from 'components/CantileverButton'
import SearchBar from 'components/SearchBar'
import { Button } from 'designSystem/components/Button'
import { DesignRunDetails } from 'features/sentinels/components/DesignRunDetails'
import { useDeleteSentinel } from 'features/sentinels/hooks/useDeleteSentinel'
import { useSentinelResult } from 'features/sentinels/hooks/useSentinelResult'
import { methodLabel } from 'features/sentinels/methodLabels'
import { shortTagId } from 'features/sentinels/tagFormat'
import { MAX_SCORE_DAYS, TARGET_METHOD_KEYS } from 'features/sentinels/types'
import { useSentinelDrivers } from 'features/sentinels/useSentinelDrivers'
import {
  SentinelSummary,
  useSentinelList,
} from 'features/sentinels/useSentinelList'
import {
  sentinelWindowRange,
  useSentinelScores,
} from 'features/timeSeries/hooks/useSentinelScores'
import { useScatterplotContext } from 'features/timeSeries/ScatterplotContext'
import { SentinelBuilderPanel } from 'features/timeSeries/sentinelStudio/SentinelBuilderPanel'
import { SentinelComparison } from 'features/timeSeries/sentinelStudio/SentinelComparison'
import { useSentinelStudio } from 'features/timeSeries/sentinelStudio/SentinelStudioContext'
import { ArrowLeft, Loader2, Trash2, X } from 'lucide-react'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import styled, { keyframes } from 'styled-components'
import { mobileInputTextStyles } from 'styles/mobileInputTextStyles'
import { BaseSensorEntry } from 'types'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
`

const HeaderBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.5rem;
  flex-shrink: 0;
`

const SearchContainer = styled.div`
  padding: 0 0.5rem 0.5rem;
  flex-shrink: 0;
`

const List = styled.div`
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0 0.5rem 0.5rem;
`

const Row = styled.div`
  border: 1px solid ${({ theme }) => theme.forecast.cardDividerBackground};
  border-radius: 0.375rem;
  padding: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
`

const UnfinishedRow = styled(Row)`
  opacity: 0.7;
`

const RowHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  cursor: pointer;
`

const RowActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  flex-wrap: wrap;
`

const DriverList = styled.ul`
  margin: 0;
  padding: 0 0 0 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
`

const DriverItem = styled.li`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const Name = styled.span`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${({ theme }) => theme.forecast.activeText};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const Detail = styled.span`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const SectionLabel = styled.div`
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${({ theme }) => theme.forecast.secondaryText};
  padding: 0.5rem 0 0.15rem;
`

const CompareBox = styled.div`
  border: 1px solid ${({ theme }) => theme.forecast.cardDividerBackground};
  border-radius: 0.375rem;
  padding: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
`

const CompareLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.7rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
  cursor: pointer;
  input {
    width: auto;
    margin: 0;
    flex: none;
  }
`

const MethodSelect = styled.select`
  flex: 1;
  min-width: 0;
  font-size: 0.75rem;
  padding: 0.25rem;
  border-radius: 0.25rem;
  border: 1px solid ${({ theme }) => theme.forecast.cardDividerBackground};
  background: ${({ theme }) => theme.forecast.cardBackground};
  color: ${({ theme }) => theme.forecast.activeText};
  ${mobileInputTextStyles}
`

const StatusNote = styled.span<{ $isError?: boolean }>`
  font-size: 0.6875rem;
  color: ${({ theme, $isError }) =>
    $isError
      ? (theme.forecast.danger ?? '#f92f06')
      : theme.forecast.secondaryText};
`

const EmptyState = styled.div`
  padding: 1rem 0.5rem;
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
  text-align: center;
`

const DetailScroll = styled.div`
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  padding: 0.5rem 0.75rem 1rem;
`

const spin = keyframes`
  to { transform: rotate(360deg); }
`

const Spinner = styled(Loader2)`
  width: 0.875rem;
  height: 0.875rem;
  animation: ${spin} 1s linear infinite;
`

const METHOD_METHOD_KEYS = [...TARGET_METHOD_KEYS]

const SentinelDriversPanel: React.FC<{
  sentinel: SentinelSummary
  tagLabelById: Map<string, string>
  selectedMethod?: string
}> = ({ sentinel, tagLabelById, selectedMethod }) => {
  const { t } = useLingui()

  const { drivers, methodStats, error, isLoading } = useSentinelDrivers(
    sentinel.id,
    sentinel.resultPath
  )

  if (isLoading) {
    return (
      <StatusNote>
        <Spinner />
        <Trans> Loading drivers…</Trans>
      </StatusNote>
    )
  }
  if (error) {
    return (
      <StatusNote $isError>
        <Trans>Failed to load drivers.</Trans>
      </StatusNote>
    )
  }

  const stats = selectedMethod ? methodStats?.[selectedMethod] : undefined

  return (
    <>
      {stats && (
        <StatusNote
          title={t`Holdout quality of the selected model method: scaled mean absolute error (lower is better), residual drift, and typical residual size`}>
          <Trans>Model quality: MAE </Trans>
          {stats.scaledMae?.toFixed(2) ?? '–'}
          {stats.drift !== null && (
            <>
              <Trans> · drift </Trans>
              {stats.drift.toFixed(3)}
            </>
          )}
          {stats.errScale !== null && (
            <>
              <Trans> · err scale </Trans>
              {stats.errScale.toFixed(3)}
            </>
          )}
        </StatusNote>
      )}
      {drivers?.length ? (
        <>
          <StatusNote>
            <Trans>Drivers</Trans>
          </StatusNote>
          <DriverList>
            {drivers.map((driver) => (
              <DriverItem key={driver} title={driver}>
                {tagLabelById.get(driver) ?? shortTagId(driver)}
              </DriverItem>
            ))}
          </DriverList>
        </>
      ) : (
        <StatusNote>
          <Trans>No drivers recorded for this sentinel.</Trans>
        </StatusNote>
      )}
    </>
  )
}

/**
 * Renders nothing — its whole job is a side effect: while mounted (the row's
 * "Show drivers" box is checked) it adds the sentinel's driver tags to the
 * chart as pens, and removes exactly those on unmount. So checking the box
 * shows the drivers alongside the target, and unchecking hides them again.
 */
const DriverPens: React.FC<{ sentinel: SentinelSummary }> = ({ sentinel }) => {
  const { setSelectedSensors, sensorEntries } = useScatterplotContext()
  const { drivers } = useSentinelDrivers(sentinel.id, sentinel.resultPath)
  const addedRef = useRef<string[]>([])
  useEffect(() => {
    if (!drivers?.length) return
    const entries = drivers
      .map((id) => sensorEntries.find((sensor) => sensor.id === id))
      .filter((entry): entry is BaseSensorEntry => entry !== undefined)
    if (!entries.length) return
    addedRef.current = entries.map((entry) => entry.id)
    setSelectedSensors((prev) => {
      const missing = entries.filter(
        (entry) => !prev.some((pen) => pen.id === entry.id)
      )
      return missing.length ? [...prev, ...missing] : prev
    })
    return () => {
      const ids = addedRef.current
      setSelectedSensors((prev) => prev.filter((pen) => !ids.includes(pen.id)))
    }
  }, [drivers, sensorEntries, setSelectedSensors])
  return null
}

/** The detail view for one saved model — its Overview/Monitor tabs, cases and
 * RCA, reusing DesignRunDetails. Continuing from a stage reopens the builder on
 * this run's context. */
const SentinelDetailView: React.FC<{ entryId: string }> = ({ entryId }) => {
  const {
    setSelectedEntryId,
    setResumeContextId,
    setBuilderOpen,
    setOpenStage,
    focusCase,
  } = useSentinelStudio()
  const { record, entry, runState, error } = useSentinelResult(entryId)
  // A case-bubble click focuses a case on this sentinel; open the detail on it.
  const detailFocusCase =
    focusCase && focusCase.entryId === entryId
      ? { caseId: focusCase.caseId, nonce: focusCase.nonce }
      : null

  const back = () => setSelectedEntryId(null)

  return (
    <Container>
      <HeaderBar>
        <CantileverButton size='small' ghost onClick={back}>
          <ArrowLeft size={14} />
          <Trans> Sentinels</Trans>
        </CantileverButton>
      </HeaderBar>
      <DetailScroll>
        {error ? (
          <StatusNote $isError>
            <Trans>Failed to load: </Trans>
            {error}
          </StatusNote>
        ) : runState === 'stalled' ? (
          <StatusNote $isError>
            <Trans>
              This run stopped reporting and never finished — its job likely
              died. Delete it and design a new one.
            </Trans>
          </StatusNote>
        ) : runState === 'running' ? (
          <EmptyState>
            <Spinner />
            <Trans> Training — this updates on its own.</Trans>
          </EmptyState>
        ) : record && record.mode === 'multitag' ? (
          <StatusNote>
            <Trans>
              This is a legacy multi-tag sentinel; design a new one to manage it
              here.
            </Trans>
          </StatusNote>
        ) : record ? (
          // A finished agentic run renders through the design flow's own panels
          // (Overview + Monitor + Cases + RCA).
          <DesignRunDetails
            record={record}
            entryId={entryId}
            files={entry?.data.files}
            initialTab={detailFocusCase ? 'monitor' : 'overview'}
            focusCase={detailFocusCase}
            onReRun={(contextId, stage) => {
              setResumeContextId(contextId)
              setOpenStage(stage ?? null)
              setSelectedEntryId(null)
              setBuilderOpen(true)
            }}
          />
        ) : (
          // No record yet and not errored/stalled: the entry or its result.json
          // is still resolving. Never leave the pane blank.
          <EmptyState>
            <Spinner />
            <Trans> Loading sentinel…</Trans>
          </EmptyState>
        )}
      </DetailScroll>
    </Container>
  )
}

/** The builder view: a back bar over the staged agentic panel. */
const SentinelBuilderView: React.FC = () => {
  const { setBuilderOpen } = useSentinelStudio()
  return (
    <Container>
      <HeaderBar>
        <CantileverButton
          size='small'
          ghost
          onClick={() => setBuilderOpen(false)}>
          <ArrowLeft size={14} />
          <Trans> Sentinels</Trans>
        </CantileverButton>
      </HeaderBar>
      <SentinelBuilderPanel />
    </Container>
  )
}

/** The saved-models list: add a band to the chart, pick a method, compare,
 * open a model's detail, or resume an unfinished design. Plus "New sentinel". */
const SentinelListView: React.FC = () => {
  const { i18n, t } = useLingui()
  const methodOptionLabel = (key: string) => methodLabel(i18n, key)

  const {
    siteID,
    timeRange,
    dataLayers,
    activeSentinels,
    addSentinel,
    removeSentinel,
    setSentinelMethod,
    sensorEntries,
    selectedSensors,
    setTimeRange,
    exitLiveTail,
  } = useScatterplotContext()

  // Adding a model frames the chart to its train+test window (plus a forecast
  // tail) so its band and split boundaries are actually in view, and carries
  // the honest split so the boundaries draw in the right spots.
  const addToChart = (sentinel: SentinelSummary) => {
    if (!sentinel.target) return
    addSentinel(sentinel.id, sentinel.target, sentinel.name, {
      windowStart: sentinel.windowStart,
      windowEnd: sentinel.windowEnd,
      splitDate: sentinel.splitDate,
    })
    const range = sentinelWindowRange(
      sentinel.windowStart,
      sentinel.windowEnd,
      selectedSensors.length + 1
    )
    if (range) {
      exitLiveTail()
      setTimeRange(range)
    }
  }
  const {
    api,
    dispatchPlan,
    setSource,
    setSentinelName,
    setNotes,
    setOpenStage,
    setBuilderOpen,
    setResumeContextId,
    setSelectedEntryId,
    compareIds,
    toggleCompare,
  } = useSentinelStudio()
  const [search, setSearch] = useState('')
  const [expandedId, setExpandedId] = useState<string | null>(null)
  // Sentinels whose driver pens are shown on the chart (the per-row toggle).
  const [showDriverIds, setShowDriverIds] = useState<Set<string>>(new Set())
  const toggleDrivers = (id: string) =>
    setShowDriverIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const tagLabelById = useMemo(() => {
    const labels = new Map<string, string>()
    for (const entry of sensorEntries) {
      const data = entry.data as { pi_tag?: string; name?: string }
      labels.set(entry.id, data.pi_tag || data.name || shortTagId(entry.id))
    }
    return labels
  }, [sensorEntries])

  const {
    sentinels,
    unfinishedDesigns,
    isLoading: isLoadingList,
    mutate: mutateList,
  } = useSentinelList(siteID)
  const { deleteSentinel, deleting } = useDeleteSentinel()
  const [deletingId, setDeletingId] = useState<string | null>(null)

  // Delete a saved sentinel (or an unfinished design) and drop it from the
  // list — keeps the master list from accumulating dead/duplicate runs. If it
  // is currently drawn on the chart, take it off first.
  const handleDelete = async (id: string, name: string) => {
    if (!window.confirm(t`Delete "${name}"? This permanently removes it.`))
      return
    setDeletingId(id)
    try {
      if (activeById.has(id)) removeSentinel(id)
      await deleteSentinel(siteID, id)
      await mutateList()
    } catch {
      // useDeleteSentinel surfaces the error; leave the row in place.
    } finally {
      setDeletingId(null)
    }
  }
  const sentinelsEnabled =
    dataLayers.find((layer) => layer.id === 'sentinels')?.enabled ?? false
  const { scores, scoreWindow } = useSentinelScores(
    siteID,
    activeSentinels,
    timeRange,
    { enabled: sentinelsEnabled }
  )

  // Chartable models: finished target-mode sentinels.
  const listable = useMemo(
    () =>
      sentinels.filter(
        (sentinel) =>
          sentinel.status === 'done' &&
          sentinel.mode === 'target' &&
          sentinel.target
      ),
    [sentinels]
  )

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase()
    if (!query) return listable
    return listable.filter(
      (sentinel) =>
        sentinel.name.toLowerCase().includes(query) ||
        (sentinel.target ?? '').toLowerCase().includes(query)
    )
  }, [listable, search])

  const activeById = useMemo(
    () =>
      new Map(activeSentinels.map((sentinel) => [sentinel.entryId, sentinel])),
    [activeSentinels]
  )

  const startNew = () => {
    api.resetContext()
    dispatchPlan({ type: 'clear' })
    setResumeContextId(null)
    setSource('instance')
    setSentinelName('')
    setNotes('')
    setOpenStage('context')
    setSelectedEntryId(null)
    setBuilderOpen(true)
  }

  const resumeDesign = (contextId: string) => {
    setResumeContextId(contextId)
    setOpenStage(null)
    setSelectedEntryId(null)
    setBuilderOpen(true)
  }

  return (
    <Container>
      <HeaderBar>
        <Name>
          <Trans>Sentinels</Trans>
        </Name>
        <Button variant='primaryRaised' onClick={startNew}>
          <Trans>New sentinel</Trans>
        </Button>
      </HeaderBar>
      <SearchContainer>
        <SearchBar
          placeholder={t`Search sentinels by name or target tag...`}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: '100%' }}
        />
      </SearchContainer>
      <List>
        {compareIds.length >= 2 && (
          <CompareBox>
            <StatusNote>
              <Trans>Comparing </Trans>
              {compareIds.length} models
            </StatusNote>
            <SentinelComparison entryIds={compareIds} />
          </CompareBox>
        )}

        {isLoadingList && (
          <EmptyState>
            <Spinner />
            <Trans> Loading sentinels…</Trans>
          </EmptyState>
        )}

        {!isLoadingList && listable.length === 0 && (
          <EmptyState>
            <Trans>No sentinels yet. Press </Trans>
            <strong>
              <Trans>New sentinel</Trans>
            </strong>
            <Trans> to build one.</Trans>
          </EmptyState>
        )}

        {filtered.map((sentinel) => {
          const active = activeById.get(sentinel.id)
          const score = active ? scores[sentinel.id] : undefined
          const methodOptions =
            score?.ok && score.response.methods
              ? Object.keys(score.response.methods)
              : METHOD_METHOD_KEYS
          const selectedMethod = active?.method ?? methodOptions[0]
          const expanded = expandedId === sentinel.id
          const isComparing = compareIds.includes(sentinel.id)
          const intervalMinutes = score?.ok
            ? (score.response.window?.interval_minutes ?? 10)
            : 10

          let inViewNote: string | null = null
          if (score?.ok && score.response.methods?.[selectedMethod]) {
            const method = score.response.methods[selectedMethod]
            const targetKey = score.response.target ?? sentinel.target ?? ''
            const residual = method.residual[targetKey] ?? []
            const errScale = method.err_scale[targetKey]
            if (errScale) {
              let scored = 0
              let outOfBand = 0
              let worst = 0
              for (const r of residual) {
                if (r === null || r === undefined) continue
                scored += 1
                const sigma = Math.abs(r) / errScale
                if (sigma >= 2) outOfBand += 1
                if (sigma > worst) worst = sigma
              }
              if (scored > 0) {
                inViewNote = t`${{ value634_34: ((outOfBand / scored) * 100).toFixed(1) }}% out of band · worst ${{ value634_98: worst.toFixed(1) }}σ`
              }
            }
          }

          return (
            <Row key={sentinel.id}>
              <RowHeader
                title={t`Show drivers`}
                onClick={() => setExpandedId(expanded ? null : sentinel.id)}>
                <Name title={sentinel.name}>{sentinel.name}</Name>
                {active ? (
                  <CantileverButton
                    iconOnly
                    ghost
                    size='small'
                    title={t`Remove sentinel from chart`}
                    onClick={(e) => {
                      e.stopPropagation()
                      removeSentinel(sentinel.id)
                    }}>
                    <X size={14} />
                  </CantileverButton>
                ) : (
                  <CantileverButton
                    size='small'
                    onClick={(e) => {
                      e.stopPropagation()
                      addToChart(sentinel)
                    }}>
                    <Trans>Add</Trans>
                  </CantileverButton>
                )}
              </RowHeader>
              <Detail title={sentinel.detail}>{sentinel.detail}</Detail>

              <RowActions>
                <Button
                  variant='secondary'
                  onClick={() => setSelectedEntryId(sentinel.id)}>
                  <Trans>Open</Trans>
                </Button>
                <CompareLabel title={t`Add to the comparison table`}>
                  <input
                    type='checkbox'
                    checked={isComparing}
                    onChange={() => toggleCompare(sentinel.id)}
                  />
                  <Trans>Compare</Trans>
                </CompareLabel>
                {active && (
                  <CompareLabel
                    title={t`Show this sentinel's driver tags on the chart too`}>
                    <input
                      type='checkbox'
                      checked={showDriverIds.has(sentinel.id)}
                      onChange={() => toggleDrivers(sentinel.id)}
                    />
                    <Trans>Show drivers</Trans>
                  </CompareLabel>
                )}
                <CantileverButton
                  iconOnly
                  ghost
                  size='small'
                  title={t`Delete this sentinel`}
                  disabled={deleting && deletingId === sentinel.id}
                  style={{ marginLeft: 'auto' }}
                  onClick={(e) => {
                    e.stopPropagation()
                    void handleDelete(sentinel.id, sentinel.name)
                  }}>
                  {deleting && deletingId === sentinel.id ? (
                    <Spinner />
                  ) : (
                    <Trash2 size={14} />
                  )}
                </CantileverButton>
              </RowActions>

              {active && showDriverIds.has(sentinel.id) && (
                <DriverPens sentinel={sentinel} />
              )}

              {expanded && (
                <SentinelDriversPanel
                  sentinel={sentinel}
                  tagLabelById={tagLabelById}
                  selectedMethod={selectedMethod}
                />
              )}
              {active && (
                <>
                  {inViewNote && (
                    <StatusNote
                      title={t`Over the scored window, with the selected method`}>
                      {inViewNote}
                    </StatusNote>
                  )}
                  <StatusNote
                    title={t`The band and its sigmas are calibrated on interval averages, so raw spikes briefer than the interval are not scored against it`}>
                    <Trans>
                      Band compares {intervalMinutes}-minute averages against
                      the model.
                    </Trans>
                  </StatusNote>
                  <MethodSelect
                    value={selectedMethod}
                    onChange={(e) =>
                      setSentinelMethod(sentinel.id, e.target.value)
                    }>
                    {methodOptions.map((method) => (
                      <option key={method} value={method}>
                        {methodOptionLabel(method)}
                      </option>
                    ))}
                  </MethodSelect>
                  {score && !score.ok && (
                    <StatusNote $isError>
                      <Trans>Scoring failed: </Trans>
                      {score.error}
                    </StatusNote>
                  )}
                  {score?.ok && scoreWindow.clamped && (
                    <StatusNote>
                      <Trans>The visible range is longer than the </Trans>
                      {MAX_SCORE_DAYS}
                      <Trans>-day scoring limit, so the band covers </Trans>
                      {scoreWindow.startDate} → {scoreWindow.endDate}
                      <Trans> (the most recent </Trans>
                      {MAX_SCORE_DAYS}{' '}
                      <Trans>
                        days). Zoom in to score the range you are viewing.
                      </Trans>
                    </StatusNote>
                  )}
                  {!sentinelsEnabled && (
                    <StatusNote>
                      <Trans>
                        The Sentinels data layer is off — enable it to see the
                        alerting range on the chart.
                      </Trans>
                    </StatusNote>
                  )}
                </>
              )}
            </Row>
          )
        })}

        {filtered.length === 0 && listable.length > 0 && (
          <EmptyState>
            <Trans>No sentinels match “</Trans>
            {search}”.
          </EmptyState>
        )}

        {unfinishedDesigns.length > 0 && (
          <>
            <SectionLabel>
              <Trans>Unfinished</Trans>
            </SectionLabel>
            {unfinishedDesigns.map((design) => (
              <UnfinishedRow key={design.entryId}>
                <RowHeader onClick={() => resumeDesign(design.contextId)}>
                  <Name title={design.name}>{design.name}</Name>
                  <Button
                    variant='secondary'
                    onClick={(e) => {
                      e.stopPropagation()
                      resumeDesign(design.contextId)
                    }}>
                    <Trans>Resume</Trans>
                  </Button>
                  <CantileverButton
                    iconOnly
                    ghost
                    size='small'
                    title={t`Delete this design`}
                    disabled={deleting && deletingId === design.entryId}
                    onClick={(e) => {
                      e.stopPropagation()
                      void handleDelete(design.entryId, design.name)
                    }}>
                    {deleting && deletingId === design.entryId ? (
                      <Spinner />
                    ) : (
                      <Trash2 size={14} />
                    )}
                  </CantileverButton>
                </RowHeader>
                <Detail title={design.detail}>{design.detail}</Detail>
              </UnfinishedRow>
            ))}
          </>
        )}
      </List>
    </Container>
  )
}

/**
 * The customer Sentinels tab: a saved-models list, the agentic builder, and a
 * model's detail (Overview / Monitor / Cases / RCA). Which one shows is driven
 * by the durable studio state, so switching sidebar tabs never loses your place.
 */
export const SentinelsTab: React.FC = () => {
  const { selectedEntryId, builderOpen } = useSentinelStudio()
  if (selectedEntryId) return <SentinelDetailView entryId={selectedEntryId} />
  if (builderOpen) return <SentinelBuilderView />
  return <SentinelListView />
}
